import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, SlidersHorizontal } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { flowerPriceRange, isFlower, glowClass } from "@/lib/sizes";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' width='400' height='400'><rect width='400' height='400' fill='#ece3d5'/><text x='50%' y='50%' fill='#a06d34' font-family='serif' font-size='20' text-anchor='middle' dominant-baseline='middle'>THE GOOD LIFE</text></svg>"
  );

export function resolveImg(url) {
  if (!url) return PLACEHOLDER;
  if (url.startsWith("/api")) return `${BACKEND_URL}${url}`;
  return url;
}

export const ProductCard = ({ product, index = 0 }) => {
  const { addItem } = useCart();
  const flower = isFlower(product);
  const range = flowerPriceRange(product);
  const soldOut = !flower && (product.stock ?? 0) <= 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      className={`group border bg-white transition-shadow duration-300 ${glowClass(product.strain_type)}`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative aspect-square overflow-hidden bg-[#f2ece1]">
          <img
            src={resolveImg(product.image_url)}
            alt={product.name}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          {product.strain_type && (
            <span className="absolute left-4 top-4 border border-black/10 bg-white/80 px-2 py-1 text-[10px] uppercase tracking-widest text-[#2b2520] backdrop-blur">
              {product.strain_type}
            </span>
          )}
        </div>
      </Link>
      <div className="p-6">
        <div className="mb-1 flex items-center justify-between">
          <span className="overline">{product.category}</span>
          {flower
            ? product.thca ? <span className="text-xs text-neutral-500">{product.thca}% THCa</span> : null
            : <span className="text-xs text-neutral-500">{product.thca}% THCA</span>}
        </div>
        <Link to={`/product/${product.id}`}>
          <h3 className="mb-1 text-xl font-bold leading-tight text-[#2b2520] transition-colors group-hover:text-[#a06d34]">
            {product.name}
          </h3>
        </Link>
        {flower && product.grow_method && (
          <span
            data-testid={`card-grow-${product.id}`}
            className="mb-2 inline-block border border-[#6ba9cd]/50 bg-[#9ecbe4]/20 px-2 py-0.5 text-[10px] uppercase tracking-widest text-[#2b6a8c]"
          >
            {product.grow_method}
          </span>
        )}
        {flower && (product.terpenes || []).length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1" data-testid={`card-terpenes-${product.id}`}>
            {product.terpenes.slice(0, 3).map((t, i) => (
              <span key={i} className="border border-[#c99a63]/40 bg-[#c99a63]/10 px-2 py-0.5 text-[10px] text-[#a06d34]">
                {t.name}{t.percentage ? ` ${t.percentage}%` : ""}
              </span>
            ))}
          </div>
        )}
        <p className="mb-4 text-xs text-neutral-500">
          {flower ? "Multiple sizes available" : soldOut ? "Sold out" : `${product.stock} in stock`}
        </p>
        <div className="flex items-center justify-between">
          <span className="text-lg font-semibold text-[#2b2520]">
            {flower
              ? range
                ? `from $${range.min.toFixed(2)}`
                : "—"
              : `$${(product.price || 0).toFixed(2)}`}
          </span>
          {flower ? (
            <Link
              to={`/product/${product.id}`}
              data-testid={`select-options-${product.id}`}
              className="flex items-center gap-1 border border-black/15 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[#2b2520] transition-colors duration-300 hover:border-[#c99a63] hover:text-[#a06d34]"
            >
              <SlidersHorizontal className="h-3 w-3" strokeWidth={2} /> Options
            </Link>
          ) : (
            <button
              data-testid={`add-to-cart-${product.id}`}
              disabled={soldOut}
              onClick={() => {
                addItem(product);
                toast.success(`${product.name} added to cart`);
              }}
              className="flex items-center gap-1 border border-black/15 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[#2b2520] transition-colors duration-300 hover:border-[#c99a63] hover:text-[#a06d34] disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-3 w-3" strokeWidth={2} /> Add
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};
